import { Box, Button, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import CustomerCareDTable from '../../../Shared/CustomerCareDTable';
import TabsEmptyState from '../../components/TabsEmptyState';
import { Add as AddIcon } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import DashboardTableLegend from '../../../Dashboard/detailPanel/DashboardTableLegend';
import FacilityStatusIcon from '../../components/FacilityStatusIcon';
import { ReactComponent as NoPatientsIcon } from '../../../../images/icons/no_patients_icon.svg';
import { parseDateToLocal } from '../../../../shared/utils';
import { TabProps } from '../FacilityDetails';
import { useDispatch, useSelector } from 'react-redux';
import {
  getPatientsList,
  getPatientsListByLocation,
} from '../../../../store/facility/actionCreators';
import { RootState } from '../../../../store';
import LoadingSpinner from '../../../Shared/LoadingSpinner';
import NewModal from '../../../Shared/NewModal';
import AddModernFacilityPatientController from './AddModernFacilityPatientController';
import {
  customError,
  customSuccess,
  lightGrey,
  warningYellow,
} from '../../../../mainTheme';

const tableLegendData = [
  {
    name: '0',
    color: customSuccess,
  },
  {
    name: '1',
    color: warningYellow,
  },
  {
    name: '2 or more',
    color: customError,
  },
];

const columns: GridColDef[] = [
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.7,
    sortable: true,
    sortComparator: (v1: any, v2: any) => {
      return (v1 ?? '').localeCompare(v2 ?? '');
    },
    renderCell: (params: any) => {
      return (
        <FacilityStatusIcon
          status={params.value as 'ok' | 'warning' | 'critical'}
        />
      );
    },
  },
  {
    field: 'patient',
    headerName: 'Patient',
    flex: 1,
    valueGetter: (params: any) =>
      `${params.row.first_name} ${params.row.last_name}`,
  },
  { field: 'customer_id', headerName: 'User number', flex: 1 },
  {
    field: 'room_number',
    headerName: 'Room number',
    flex: 1,
    renderCell: (params: any) => (params.value ? params.value : 'N/A'),
  },
  {
    field: 'next_med_event',
    headerName: 'Next med event',
    flex: 1.5,
    renderCell: (params: any) =>
      params.value
        ? parseDateToLocal(params.value, 'yyyy-MM-dd hh:mm a')
        : 'N/A',
  },
  {
    field: 'last_login_at',
    headerName: 'Last online',
    flex: 1,
    renderCell: (params: any) =>
      params.value
        ? parseDateToLocal(params.value, 'yyyy-MM-dd hh:mm a')
        : 'N/A',
  },
];

const PatientsTab: React.FC<TabProps> = ({
  parentFacilityId,
  locationId,
  isFacilityAdmin,
}) => {
  const dispatch = useDispatch();

  const { patientsList, isLoading } = useSelector(
    (state: RootState) => state.facility,
  );

  const [isAddPatientModalOpen, setIsAddPatientModalOpen] = useState(false);
  const [currentModalStep, setCurrentModalStep] =
    useState<string>('patient_details');

  useEffect(() => {
    if (isFacilityAdmin && locationId) {
      dispatch(getPatientsListByLocation(locationId));
    } else if (parentFacilityId) {
      if (locationId) {
        dispatch(getPatientsList(parentFacilityId, locationId));
      } else {
        dispatch(getPatientsList(parentFacilityId));
      }
    }
  }, [dispatch, parentFacilityId, locationId, isFacilityAdmin]);

  const handleAddPatientClick = () => {
    setIsAddPatientModalOpen(true);
    setCurrentModalStep('patient_details'); // Reset step when opening modal
  };

  const handleCloseAddPatientModal = () => {
    setIsAddPatientModalOpen(false);
    setCurrentModalStep('patient_details'); // Reset step when closing modal
  };

  const handlePatientAddSuccess = () => {
    if (isFacilityAdmin && locationId) {
      dispatch(getPatientsListByLocation(locationId));
    } else if (parentFacilityId) {
      if (locationId) {
        dispatch(getPatientsList(parentFacilityId, locationId));
      } else {
        dispatch(getPatientsList(parentFacilityId));
      }
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      {patientsList && patientsList.length > 0 ? (
        <>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" gap={4}>
              <Box display="flex" gap={4} alignItems="center">
                <DashboardTableLegend tableLegendData={tableLegendData} />
                <Typography
                  sx={{
                    color: lightGrey,
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  missed med events in the last 24 hrs
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              color="secondary"
              onClick={handleAddPatientClick}
              sx={{ height: '40px' }}
            >
              Add new patient
            </Button>
          </Box>
          <CustomerCareDTable
            rows={patientsList}
            columns={columns}
            pageSize={5}
            defaultSortField="status"
            defaultSortDirection="desc"
            hideViewAllButton
            idField="customer_id"
          />
        </>
      ) : (
        <TabsEmptyState
          icon={<NoPatientsIcon />}
          title="There are no patients"
          description="Add a new patient to get started."
          buttonText="Add new patient"
          onButtonClick={handleAddPatientClick}
        />
      )}

      {isAddPatientModalOpen && parentFacilityId !== undefined && (
        <NewModal
          showCloseX
          title="Add new patient"
          onClose={handleCloseAddPatientModal}
          maxWidth={currentModalStep === 'patient_details' ? 'sm' : 'xs'}
        >
          <AddModernFacilityPatientController
            onClose={handleCloseAddPatientModal}
            parentFacilityId={parentFacilityId}
            locationId={locationId}
            onSuccess={handlePatientAddSuccess}
            onStepChange={setCurrentModalStep}
          />
        </NewModal>
      )}
    </Box>
  );
};

export default PatientsTab;
